// === SAA:UI:HELP-CARD ===
// Single source for the advisor ("Hulp Nodig?") contact card shown on every flow
// screen. Contact details come from config (customer.contact), copy from the
// per-brand config texts.sidebar block with the shared screens.paymentInfo.sidebar
// i18n keys as fallback. Photo and status line are optional slots: no configured
// value, no element — never ship placeholder data in them. showPhoto (default
// on) lets a layout move the photo to the trust card instead. Styling is the
// shared commonStyles help/contact block, so this card is identical everywhere.

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useConfig, formatPhoneLabel } from '@sluitstraat/core';
import { commonStyles, helpSidebarLayoutStyles } from '../styles/commonStyles';
import {
  getSidebarTexts,
  getContactHours,
  resolveSidebarText,
  resolveOptionalSidebarText,
} from '../config/sidebarContent';
import { AdvisorPhoto } from './AdvisorPhoto';
import { TrustCard } from './TrustCard';
import { SubscriptionCard } from './SubscriptionCard';
import { SelectionCard } from './SelectionCard';

interface HelpCardProps {
  className?: string;
  showPhoto?: boolean;
}

export const HelpCard: React.FC<HelpCardProps> = ({ className, showPhoto = true }) => {
  const { t, i18n } = useTranslation();
  const { config } = useConfig();
  const sidebar = getSidebarTexts(config);

  const phone = config.customer?.contact?.phone?.main || t('screens.paymentInfo.sidebar.phone');
  const email = config.customer?.contact?.email || t('screens.paymentInfo.sidebar.email');
  const hours = getContactHours(config) || t('common.contactHours');
  const eyebrow = resolveSidebarText(t, i18n, sidebar.advisorEyebrowKey, 'sidebar.advisor.eyebrow');
  const title = resolveSidebarText(t, i18n, sidebar.advisorTitleKey, 'sidebar.advisor.title');
  const description = resolveSidebarText(t, i18n, sidebar.advisorTextKey, 'sidebar.advisor.text');
  const statusText = resolveOptionalSidebarText(t, i18n, sidebar.statusTextKey);

  return (
    <div style={commonStyles.helpCard} className={className} tabIndex={-1}>
      {showPhoto && <AdvisorPhoto />}
      {statusText && (
        <div style={commonStyles.advisorStatusRow}>
          <span style={commonStyles.advisorStatusDot} aria-hidden="true" />
          <span>{statusText}</span>
        </div>
      )}
      <p style={commonStyles.eyebrowLabel}>{eyebrow}</p>
      <h3 style={commonStyles.helpTitle}>{title}</h3>
      <p style={commonStyles.helpDescription}>{description}</p>

      <div style={commonStyles.contactInfo}>
        <div style={commonStyles.contactItem}>
          <svg style={commonStyles.contactIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" tabIndex={-1} aria-hidden="true">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
          </svg>
          <a href={`tel:${phone}`} style={commonStyles.contactLink} tabIndex={-1}>{formatPhoneLabel(phone)}</a>
        </div>
        <div style={commonStyles.contactItem}>
          <svg style={commonStyles.contactIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" tabIndex={-1} aria-hidden="true">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
            <polyline points="22,6 12,13 2,6"></polyline>
          </svg>
          <a href={`mailto:${email}`} style={commonStyles.contactLink} tabIndex={-1}>{email}</a>
        </div>
        <div style={commonStyles.contactItem}>
          <svg style={commonStyles.contactIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" tabIndex={-1} aria-hidden="true">
            <circle cx="12" cy="12" r="10"></circle>
            <polyline points="12 6 12 12 16 14"></polyline>
          </svg>
          <span style={commonStyles.contactText}>{hours}</span>
        </div>
      </div>
    </div>
  );
};

/**
 * Right-hand sidebar column, top to bottom: selection card (steps 2+ only — step 1
 * reports its selection in the bottom bar instead), advisor card (with the advisor
 * photo), USP/trust card (opt-in via `showTrust`: product selection + client information
 * only, not the product screens), serviceabonnement card (always, self-hides when the
 * fee is off). Both advisor/trust cards take `showPhoto`, so the photo can move to the
 * trust card later without touching the components.
 * Stacks below the content on mobile via the `help-layout-aside` rules in
 * index.css; on desktop the same rules make it sticky so it follows long forms.
 */
export const HelpSidebar: React.FC<{ showSelection?: boolean; showTrust?: boolean }> = ({ showSelection = true, showTrust = false }) => (
  <aside style={helpSidebarLayoutStyles.aside} className="help-layout-aside help-layout-aside--sticky" tabIndex={-1}>
    {showSelection && <SelectionCard />}
    <HelpCard />
    {showTrust && <TrustCard />}
    <SubscriptionCard />
  </aside>
);

/**
 * Two-column wrapper for screens that don't have a flex-row layout yet: main
 * content on the left, the HelpCard pinned in the same right-hand column as the
 * payment/acceptance/confirmation sidebars. Stacks on mobile (`help-layout` rules).
 *
 * `stackEarly` stacks below 1024px instead of 768px — for screens whose main
 * content (e.g. multi-field form rows) needs more width than half a tablet.
 */
export const WithHelpSidebar: React.FC<{ children: React.ReactNode; stackEarly?: boolean; showSelection?: boolean; showTrust?: boolean }> = ({ children, stackEarly, showSelection, showTrust }) => (
  <div style={helpSidebarLayoutStyles.wrapper} className={`help-layout${stackEarly ? ' help-layout--early-stack' : ''}`}>
    <div style={helpSidebarLayoutStyles.main} className="help-layout-main">{children}</div>
    <HelpSidebar showSelection={showSelection} showTrust={showTrust} />
  </div>
);
